import { initializeApp, type FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { getFunctions, type Functions } from 'firebase/functions';
import { firebaseConfig, RECAPTCHA_SITE_KEY, FUNCTIONS_REGION } from './env';

let app: FirebaseApp | undefined;
let functions: Functions | undefined;

/**
 * Idempotent init. App Check is set up here rather than lazily because both
 * callables enforce it: without a token they 401, so there is no useful state
 * where the app runs and App Check does not.
 *
 * ORDER IS LOAD-BEARING. The module-level `app` is assigned only AFTER
 * `initializeAppCheck`. Assigned first, a throw there would leave every later
 * call returning a truthy cached `app` from the top of this function, skipping
 * App Check forever while both callables 401 with nothing explaining why.
 *
 * What this does NOT do is surface such a failure. `initializeAppCheck` is called
 * un-awaited and un-caught, and callers catch broadly and render a generic
 * message, so a real init failure reads as "Something went wrong on our end".
 * The one case that IS made visible is a missing site key, via the warn below.
 */
export function getFirebaseApp(): FirebaseApp | undefined {
  const config = firebaseConfig();
  if (!config) return undefined;
  if (app) return app;

  const initialized = initializeApp(config);
  if (RECAPTCHA_SITE_KEY) {
    initializeAppCheck(initialized, {
      provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } else {
    // Every callable enforces App Check, so a production build missing this
    // key does not merely lose a nice-to-have: `resolveAddress` and
    // `estimate` both 401 for every customer. Loud in the console, since
    // this is a build misconfiguration, not a runtime condition a customer
    // can hit.
    console.warn(
      '[studio-widget] VITE_RECAPTCHA_SITE_KEY is missing or unset; App Check will not run and every callable will 401.',
    );
  }
  app = initialized;
  return app;
}

/** The Functions instance, or undefined in mock mode. Initialises App Check on first call. */
export function getFirebaseFunctions(): Functions | undefined {
  const a = getFirebaseApp();
  if (!a) return undefined;
  functions ??= getFunctions(a, FUNCTIONS_REGION);
  return functions;
}
